import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';
import { TrendingUp, TrendingDown, LucideIcon } from 'lucide-react';

const cardVariants = cva(
  'relative overflow-hidden rounded-xl border transition-all duration-300',
  {
    variants: {
      variant: {
        default: 'bg-white border-slate-200 hover:shadow-lg hover:-translate-y-0.5',
        gradient: 'border-transparent text-white',
        outlined: 'bg-white border-slate-200 hover:border-slate-300',
        success: 'bg-gradient-to-br from-emerald-500 to-teal-600 border-transparent text-white hover:shadow-lg hover:-translate-y-0.5',
        warning: 'bg-gradient-to-br from-amber-400 to-orange-500 border-transparent text-white hover:shadow-lg hover:-translate-y-0.5',
        info: 'bg-gradient-to-br from-blue-500 to-indigo-600 border-transparent text-white hover:shadow-lg hover:-translate-y-0.5',
        danger: 'bg-gradient-to-br from-red-500 to-rose-600 border-transparent text-white hover:shadow-lg hover:-translate-y-0.5',
      },
      size: {
        default: 'p-6',
        compact: 'p-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const iconContainerVariants = cva(
  'flex items-center justify-center rounded-xl transition-transform',
  {
    variants: {
      variant: {
        default: 'bg-blue-50 text-blue-600',
        gradient: 'bg-white/20 text-white',
        outlined: 'bg-slate-50 text-slate-600',
        success: 'bg-white/20 text-white',
        warning: 'bg-white/20 text-white',
        info: 'bg-white/20 text-white',
        danger: 'bg-white/20 text-white',
      },
      size: {
        default: 'h-12 w-12',
        compact: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactElement<LucideIcon>;
  variant?: 'default' | 'gradient' | 'outlined' | 'success' | 'warning' | 'info' | 'danger';
  gradientColors?: string; // e.g., 'from-blue-50 to-indigo-50'
  size?: 'default' | 'compact';
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  className?: string;
}

export function StatCard({
  title,
  value,
  description,
  icon,
  variant = 'default',
  gradientColors,
  size = 'default',
  trend,
  className,
}: StatCardProps) {
  const isColoredCard = variant !== 'default' && variant !== 'outlined';
  const isGradientBg = variant === 'gradient' && gradientColors;

  return (
    <Card
      className={cn(
        cardVariants({ variant, size }),
        isGradientBg && `bg-gradient-to-br ${gradientColors}`,
        'group',
        className
      )}
    >
      {/* Decorative background elements for colored cards */}
      {isColoredCard && (
        <>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        </>
      )}

      <CardContent className={cn('relative', size === 'default' ? 'p-6' : 'p-4')}>
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            <p
              className={cn(
                'text-sm font-medium',
                isColoredCard ? 'text-white/80' : 'text-slate-600',
                size === 'compact' && 'text-xs'
              )}
            >
              {title}
            </p>

            <div className="flex items-baseline gap-2">
              <span
                className={cn(
                  'font-bold tracking-tight',
                  isColoredCard ? 'text-white' : isGradientBg ? 'text-blue-600' : 'text-slate-900',
                  size === 'default' ? 'text-3xl' : 'text-2xl'
                )}
              >
                {value}
              </span>
            </div>

            {description && (
              <p
                className={cn(
                  'text-sm',
                  isColoredCard ? 'text-white/70' : 'text-slate-500',
                  size === 'compact' && 'text-xs'
                )}
              >
                {description}
              </p>
            )}

            {trend && (
              <div className="flex items-center gap-1 pt-1">
                {trend.direction === 'up' ? (
                  <TrendingUp className={cn('h-4 w-4', isColoredCard ? 'text-white' : 'text-emerald-500')} />
                ) : (
                  <TrendingDown className={cn('h-4 w-4', isColoredCard ? 'text-white' : 'text-red-500')} />
                )}
                <span
                  className={cn(
                    'text-sm font-medium',
                    isColoredCard
                      ? 'text-white'
                      : trend.direction === 'up'
                      ? 'text-emerald-600'
                      : 'text-red-600'
                  )}
                >
                  {trend.direction === 'up' ? '+' : '-'}
                  {Math.abs(trend.value)}%
                </span>
              </div>
            )}
          </div>

          <div className={cn(iconContainerVariants({ variant, size }), 'group-hover:scale-110')}>
            {React.cloneElement(icon, {
              className: cn('text-current', size === 'default' ? 'h-5 w-5' : 'h-4 w-4'),
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
